<template>
  <div class="units-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Единицы измерения груза"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/cargo_units/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <!-- Форма создания -->
      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card unit-form-wrap">
          <h4 class="form-title">Создание единицы измерения</h4>
          <CargoUnitForm
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="units.length === 0" class="no-data">
        Реестр единиц измерения груза пуст
      </div>

      <ul v-else class="units-list">
        <BaseSettingsListItemCard
          v-for="unit in localUnits"
          :key="unit.id"
          :enabled="unit.enabled"
          :is-editing="editingUnitId === unit.id"
          :is-loading="isLoading"
          :created-at="unit.created_at"
          :updated-at="unit.updated_at"
          edit-title="Редактирование единицы измерения"
          @edit="startEdit(unit)"
          @toggle="toggleUnit(unit)"
        >
          <template #edit>
            <CargoUnitForm
              :initial-data="unit"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(unit.id, payload)"
              @cancel="editingUnitId = null"
            />
          </template>

          <div class="unit-top">
            <span class="unit-title">
              {{ unit.title || "—" }}
            </span>
            <span class="unit-code">{{ unit.code }}</span>
          </div>
          <div class="unit-field">
            <span class="unit-field-label">
              Короткое обозначение:
            </span>
            <span class="unit-field-value">
              {{ unit.registry_short || "—" }}
            </span>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <!-- Пагинация -->
      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useCargoUnitStore } from "@/stores/cargoUnitStore";
import { useNotificationStore } from "@/stores/notificationStore";

import CargoUnitForm from "@/components/Cargo/CargoUnitForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "CargoUnitsPage",
  components: {
    CargoUnitForm,
    PaginationComponent,
  },
  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingUnitId: null,
      localUnits: [],
    };
  },
  computed: {
    ...mapState(useCargoUnitStore, [
      "units",
      "isLoading",
      "pagination",
    ]),
    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadUnits(newPage);
      },
    },
  },
  methods: {
    ...mapActions(useCargoUnitStore, [
      "getUnitsRequest",
      "createUnit",
      "updateUnit",
      "unitEnable",
      "unitDisable",
    ]),

    async loadUnits(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.getUnitsRequest({ page: pageNumber });
        this.localUnits = [...this.units];
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateLocalUnit(updatedUnit) {
      const target = this.localUnits.find(
        (u) => u.id === updatedUnit.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedUnit);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingUnitId = null;
      }
    },

    startEdit(unit) {
      this.editingUnitId = unit.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createUnit(payload);
        this.localUnits = [...this.units];
        this.showCreateForm = false;
        this.notificationStore.showSuccess(
          "Единица измерения создана"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateUnit(id, payload);
        this.localUnits = [...this.units];
        this.editingUnitId = null;
        this.notificationStore.showSuccess(
          "Единица измерения обновлена"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleUnit(unit) {
      try {
        if (unit.enabled) {
          await this.unitDisable(unit.id);
          this.updateLocalUnit({ ...unit, enabled: false });
          this.notificationStore.showSuccess(
            "Единица измерения отключена"
          );
        } else {
          await this.unitEnable(unit.id);
          this.updateLocalUnit({ ...unit, enabled: true });
          this.notificationStore.showSuccess(
            "Единица измерения включена"
          );
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },
  async created() {
    await this.loadUnits(this.$route.query.page);
  },
};
</script>

<style scoped>
.units-container {
  flex-grow: 1;
}

.unit-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.units-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.unit-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.unit-title {
  font-weight: 600;
  font-size: 17px;
}

.unit-code {
  color: #666;
}

.unit-field {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
}

.unit-field-label {
  color: #6b7280;
}

.unit-field-value {
  color: var(--color-dark);
}
</style>
